import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { secondsToMinutes } from 'date-fns';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { Button, IconButton } from '@mui/material';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import HelpIcon from '@mui/icons-material/Help';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import FavoriteTwoToneIcon from '@mui/icons-material/FavoriteTwoTone';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LanguageIcon from '@mui/icons-material/Language';

import { MIN_SHOW_RATING_REVIEWS, routeNames } from '../../../util/Constants';
import useClasses from '../../../hooks/legacy/useClasses';
import useTotalCartPrice from '../../../util/hooks/UseCart';
import getAllTimeOptions from '../../../util/GetTimeOptions';
import getLocationsDistance from '../../../util/GetLocationsDistance';

import styles from './RestaurantHeader.style';
import { getRatings } from '../../../util/Helpers';
import { setFavoriteRequest } from '../../../redux/legacy/reducers/ducks/legacy/ProfileDuck';

function RestaurantHeader() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const navigate = useNavigate();
  const cartData = useTotalCartPrice();
  const { feesArray } = cartData;

  // TODO: this should be calculated correctly
  const isRestaurantOpen = true;

  const {
    restaurants,
    address,
    orderType,
    time,
    date,
    accessToken,
    selectedRestaurant,
    selectedRestaurantDetails,
    selectedRestaurantMenu,
    history,
  } = useSelector(
    ({
      restaurants: restaurantData,
      order,
      authorization,
      restaurant,
      address: selectedAddress,
      time: selectedTime,
      profile,
    }) => ({
      restaurants: restaurantData,
      orderType: order?.type,
      address: selectedAddress.selectedAddress,
      selectedRestaurant: restaurantData.selectedRestaurant,
      selectedRestaurantDetails: restaurant?.selectedRestaurantData,
      selectedRestaurantMenu: restaurant?.selectedRestaurantMenu,
      time: selectedTime.selectedTime,
      date: selectedTime.selectedDate,
      accessToken: authorization.accessToken,
      history: profile.orders,
    })
  );

  const selectedTime = useMemo(() => time, [time]);
  const selectedDate = useMemo(() => date, [date]);
  const selectedType = useMemo(() => orderType, [orderType]);
  const isDelivery = useMemo(
    () => orderType?.label === 'Delivery',
    [orderType]
  );
  const isAsap = useMemo(() => selectedTime?.type === 'asap', [selectedTime]);
  const [currentDelays, setCurrentDelays] = useState({ min: 0, max: 0 });

  const restaurantUserAttributes = useMemo(
    () =>
      selectedRestaurantDetails
        ? selectedRestaurantDetails.user_attributes
        : null,
    [selectedRestaurantDetails]
  );

  const dispatch = useDispatch();

  const [backgroundImage, setBackgroundImage] = useState(null);
  const [todaysTime, setTodaysTime] = useState('');

  const getSelectedDayOpenings = () => {
    const dayOfWeek = date?.dayOfWeek ?? 0;
    const dayOfWeekDigit = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const restaurantOrderType =
      selectedRestaurantDetails?.schedules[selectedType.value];

    return restaurantOrderType?.[dayOfWeekDigit]?.openings;
  };

  useEffect(() => {
    const restaurantOpenings = getSelectedDayOpenings();

    setTodaysTime(getAllTimeOptions(restaurantOpenings));
  }, [
    selectedRestaurantDetails,
    selectedType?.value,
    time?.value,
    date?.value,
  ]);

  useEffect(() => {
    if (selectedRestaurantDetails) {
      if (selectedRestaurantDetails?.media) {
        setBackgroundImage(
          selectedRestaurantDetails?.media
            ?.consumer_restaurant_page_background || ''
        );
      }

      if (
        selectedRestaurantMenu?.delays &&
        selectedRestaurantDetails?.delays &&
        selectedRestaurantDetails?.windows &&
        orderType?.value
      ) {
        const min =
          orderType.value === 'delivery'
            ? secondsToMinutes(selectedRestaurantMenu?.delays?.route || 0)
            : secondsToMinutes(
                selectedRestaurantDetails?.delays[orderType.value] || 0
              );
        const max =
          min +
          secondsToMinutes(
            selectedRestaurantDetails?.windows[orderType.value] || 0
          );

        setCurrentDelays({ min, max });
      }
    }
  }, [selectedRestaurantDetails, selectedRestaurantMenu, orderType]);

  const distance =
    Math.round(
      getLocationsDistance(
        address?.latitude,
        address?.longitude,
        selectedRestaurantDetails?.latitude,
        selectedRestaurantDetails?.longitude
      ) * 10
    ) / 10;

  const fees = Number(
    restaurants.restaurants?.[selectedRestaurant]?.fees?.['Delivery Fee'] ||
      (feesArray && feesArray[1].amount) ||
      0
  ).toFixed(2);

  const feesOrDistance = () => {
    let text = '';
    if (isDelivery && fees) text = `$${fees}`;
    else if (isDelivery && !fees) text = 'No';
    else if (!isDelivery && !address?.latitude)
      text = (
        <Tooltip title="Please enter your address to learn the distance">
          <HelpIcon data-test-id="fee-helper-icon" size="small" />
        </Tooltip>
      );
    else text = distance;

    return text;
  };

  const onFavorite = useCallback(() => {
    if (!selectedRestaurantDetails || !selectedRestaurant) {
      return null;
    }

    return dispatch(
      setFavoriteRequest({
        id: selectedRestaurant,
        favorite: !restaurantUserAttributes?.favorite,
        type: 'restaurant',
      })
    );
  }, [
    dispatch,
    selectedRestaurantDetails,
    accessToken,
    restaurantUserAttributes,
  ]);

  const addressAndOpen =
    selectedRestaurantDetails?.address && todaysTime ? (
      <>
        <Box display="flex" alignItems="center">
          <LocationOnIcon sx={{ mr: 2, width: 22, height: 22 }} />
          <Typography mb={0.5}>
            {selectedRestaurantDetails?.address},{' '}
            {selectedRestaurantDetails?.city},{' '}
            {selectedRestaurantDetails?.state}{' '}
            {selectedRestaurantDetails?.zipcode}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <WatchLaterIcon sx={{ mr: 2, width: 20, height: 20 }} />
          <Typography varaint="subtitle1" sx={{ fontWeight: 700 }}>
            Opening Hours:
          </Typography>
          <Typography style={{ fontWeight: 500 }}>{todaysTime}</Typography>
        </Box>
        {selectedRestaurantDetails?.website && (
          <Box display="flex" alignItems="center">
            <LanguageIcon sx={{ mr: 2, width: 20, height: 20 }} />
            <Typography style={{ fontWeight: 500 }}>
              <a
                data-test-id="restaurant-website"
                href={selectedRestaurantDetails?.website}
              >
                {selectedRestaurantDetails?.website}
              </a>
            </Typography>
          </Box>
        )}
      </>
    ) : (
      <Stack spacing={1}>
        <Skeleton
          variant="rounded"
          width={300}
          height={24}
          sx={{ borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width={260}
          height={24}
          sx={{ borderRadius: 1 }}
        />
      </Stack>
    );

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {backgroundImage === null ? (
          <Skeleton
            className={`${classes.backgroundImage}`}
            variant="rectangular"
          />
        ) : (
          <>
            {accessToken && (
              <IconButton
                data-test-id="favorite-icon-button"
                onClick={onFavorite}
                sx={{ position: 'absolute', top: 0, right: 10 }}
              >
                {history?.length > 0 &&
                  (restaurantUserAttributes?.favorite ? (
                    <FavoriteIcon className={classes.activeFavoriteIcon} />
                  ) : (
                    <FavoriteTwoToneIcon className={classes.favoriteIcon} />
                  ))}
              </IconButton>
            )}
            {!isRestaurantOpen && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                    borderRadius: 2,
                    [theme.breakpoints.down('sm')]: {
                      borderRadius: 0,
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    left: '15px',
                    textAlign: 'left',
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<ChevronLeftIcon />}
                    onClick={() => navigate(routeNames.restaurants)}
                    data-test-id="restaurantClosedButton"
                    aria-label="restaurant is closed button"
                  >
                    <Typography variant="body1">Order Ahead</Typography>
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
      <Box px={{ xs: 3, sm: 0 }} pt={{ xs: 2, lg: 3 }}>
        <Stack spacing={1}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            mb={2}
            justifyContent="space-between"
          >
            <Box>
              {selectedRestaurantDetails?.name ? (
                <Typography variant="h4" mb={{ xs: 0, md: 1 }}>
                  {selectedRestaurantDetails?.name}
                </Typography>
              ) : (
                <Skeleton width={350} height={40} />
              )}
              {selectedRestaurantDetails?.price_level ||
              selectedRestaurantDetails?.ratings?.reviews ? (
                <Box display="flex" alignItems="center" my={1}>
                  {selectedRestaurantDetails?.price_level && (
                    <Typography color="primary.dark" variant="body2">
                      {'$'.repeat(selectedRestaurantDetails?.price_level)}
                    </Typography>
                  )}
                  {selectedRestaurantDetails?.price_level &&
                    selectedRestaurantDetails?.ratings?.reviews &&
                    parseInt(selectedRestaurantDetails.ratings.reviews, 10) >=
                      MIN_SHOW_RATING_REVIEWS && (
                      <FiberManualRecordIcon className={classes.priceIcon} />
                    )}
                  {selectedRestaurantDetails?.ratings?.reviews &&
                    selectedRestaurantDetails.ratings.reviews >=
                      MIN_SHOW_RATING_REVIEWS && (
                      <>
                        <StarIcon className={classes.starIcon} />
                        <Typography color="primary.dark" variant="body2">
                          {selectedRestaurantDetails?.ratings?.stars
                            ? getRatings(
                                selectedRestaurantDetails.ratings.stars
                              )
                            : '-'}{' '}
                          ({selectedRestaurantDetails?.ratings?.reviews || 0})
                        </Typography>
                      </>
                    )}
                </Box>
              ) : null}
              {selectedRestaurantDetails?.tags.length > 0 ? (
                selectedRestaurantDetails?.tags.map((tag) => (
                  <Chip
                    sx={{ mr: 1, mb: 1 }}
                    size="small"
                    label={tag.name}
                    key={tag.name}
                  />
                ))
              ) : (
                <Stack spacing={1} direction="row">
                  <Skeleton height={24} width={80} variant="rounded" />
                  <Skeleton height={24} width={80} variant="rounded" />
                  <Skeleton height={24} width={80} variant="rounded" />
                </Stack>
              )}
              <Box display={{ xs: 'none', md: 'unset' }}>{addressAndOpen}</Box>
            </Box>
            <Box display={{ xs: 'unset', md: 'none' }}>{addressAndOpen}</Box>
            <Stack
              direction="column"
              spacing={1}
              className={classes.orderTypeContainer}
              p={1}
              borderRadius={2}
              justifyContent="center"
            >
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                {isDelivery ? <DirectionsCarIcon /> : <DirectionsWalkIcon />}
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  For {orderType.label}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                }}
              >
                {isAsap ? (
                  <>
                    <Typography
                      varaint="subtitle1"
                      color="text.secondary"
                      mr={2}
                    >
                      {isDelivery ? 'Delivery time:' : 'Ready for pickup:'}
                    </Typography>
                    <Typography varaint="subtitle1" sx={{ fontWeight: 600 }}>
                      {`${currentDelays.min}-${currentDelays.max} min`}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography varaint="subtitle1" color="text.secondary">
                      {`${selectedDate?.label}: `}
                    </Typography>
                    <Typography varaint="subtitle1" sx={{ fontWeight: 600 }}>
                      {selectedTime?.label}
                    </Typography>
                  </>
                )}
              </Box>
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                }}
              >
                <Typography varaint="subtitle1" color="text.secondary">
                  {isDelivery ? 'Delivery fee:' : 'Miles away:'}
                </Typography>
                <Typography varaint="subtitle1" sx={{ fontWeight: 600 }}>
                  {feesOrDistance()}
                </Typography>
              </Box>
            </Stack>
          </Stack>
          <Divider />
        </Stack>
      </Box>
    </>
  );
}

export default RestaurantHeader;
